/**
 * Family Member Schema Definition
 * Defines the structure and validation for family member documents in Firestore
 */

/**
 * Family member document schema
 * @typedef {Object} FamilyMemberSchema
 * @property {string} userId - Reference to user document
 * @property {string} firstName - First name
 * @property {string} lastName - Last name
 * @property {string} email - Email address
 * @property {string} familyRole - Role in family (e.g., parent, child, spouse)
 * @property {Array<Object>} relationships - Array of relationships with other family members
 * @property {Date} joinedAt - When member joined the family
 * @property {boolean} pendingApproval - Whether membership is pending approval
 * @property {boolean} hasVaultAccess - Whether member has access to family vault
 * @property {Object} permissions - Family-specific permissions
 * @property {string} [profilePicturePath] - Path to profile picture in storage
 * @property {string} [bio] - Member's bio
 * @property {Object} [contactInfo] - Additional contact information
 */

/**
 * Default values for new family member documents
 */
export const defaultFamilyMemberValues = {
  familyRole: 'member',
  relationships: [],
  joinedAt: new Date(),
  pendingApproval: false,
  hasVaultAccess: false,
  permissions: {
    canInviteMembers: false,
    canManageDocuments: false,
    canViewVault: false
  }
};

/**
 * Required fields for family member documents
 */
export const requiredFamilyMemberFields = [
  'userId',
  'firstName',
  'lastName',
  'email',
  'familyRole',
  'joinedAt'
];

/**
 * Validates a family member document against the schema
 * @param {Object} memberData - Family member data to validate
 * @returns {Object} Validation result with isValid and errors
 */
export const validateFamilyMemberData = (memberData) => {
  const errors = [];
  
  // Check required fields
  requiredFamilyMemberFields.forEach(field => {
    if (!memberData[field]) {
      errors.push(`Missing required field: ${field}`);
    }
  });
  
  // Validate email format
  if (memberData.email && !isValidEmail(memberData.email)) {
    errors.push('Invalid email format');
  }
  
  // Validate date fields
  if (memberData.joinedAt && !isValidDate(memberData.joinedAt)) {
    errors.push('Invalid joinedAt date format');
  }
  
  // Validate relationships array
  if (memberData.relationships && !Array.isArray(memberData.relationships)) {
    errors.push('Relationships must be an array');
  }
  
  return {
    isValid: errors.length === 0,
    errors
  };
};

/**
 * Helper function to validate email format
 * @param {string} email - Email to validate
 * @returns {boolean} Whether email is valid
 */
const isValidEmail = (email) => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
};

/**
 * Helper function to validate date format
 * @param {Date|string|number} date - Date to validate
 * @returns {boolean} Whether date is valid
 */
const isValidDate = (date) => {
  if (date instanceof Date) return !isNaN(date);
  if (typeof date === 'string' || typeof date === 'number') {
    const parsedDate = new Date(date);
    return !isNaN(parsedDate);
  }
  return false;
};

/**
 * Creates a new family member document with default values
 * @param {Object} memberData - Initial family member data
 * @returns {Object} Complete family member document with default values
 */
export const createFamilyMemberDocument = (memberData) => {
  const now = new Date();
  
  return {
    ...defaultFamilyMemberValues,
    ...memberData,
    joinedAt: memberData.joinedAt || now
  };
}; 